import React, { useState } from 'react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { Milestone } from '../../types/contract';
import { useToast } from '../../components/UI/Toast';

interface RevisionRequestProps {
  isOpen: boolean;
  onClose: () => void;
  milestone: Milestone;
  onApprove: (milestoneId: string) => void;
  onRequestRevision: (milestoneId: string, feedback: string) => void;
}

export const RevisionRequest: React.FC<RevisionRequestProps> = ({
  isOpen,
  onClose,
  milestone,
  onApprove,
  onRequestRevision,
}) => {
  const { showToast } = useToast();
  const [action, setAction] = useState<'approve' | 'revision'>('approve');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (action === 'revision' && feedback.trim() === '') {
      showToast('请提供修改意见', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      if (action === 'approve') {
        await onApprove(milestone.milestone_id);
      } else {
        await onRequestRevision(milestone.milestone_id, feedback);
      }
      onClose();
      // Reset form
      setAction('approve');
      setFeedback('');
    } catch (error) {
      console.error('Failed to submit review:', error);
      showToast('提交失败，请重试', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setAction('approve');
      setFeedback('');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="审核交付物"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            取消
          </Button>
          <Button
            variant={action === 'approve' ? 'primary' : 'outline'}
            onClick={handleSubmit}
            disabled={isSubmitting || (action === 'revision' && feedback.trim() === '')}
          >
            {isSubmitting
              ? '提交中...'
              : action === 'approve'
                ? '批准并付款'
                : '请求修改'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Milestone Info */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h4 className="font-semibold text-slate-900 mb-1">{milestone.title}</h4>
          <p className="text-sm text-slate-600">{milestone.description}</p>
          <p className="text-xs text-slate-500 mt-2">
            提交次数：{milestone.submission_count} 次
          </p>
        </div>

        {/* Deliverables */}
        {milestone.deliverable_urls.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              交付物列表
            </label>
            <div className="space-y-2">
              {milestone.deliverable_urls.map((url, index) => {
                // Drop signed-URL query params before extracting the filename,
                // and URL-decode in case it contains spaces or Chinese chars.
                const baseUrl = url.split('?')[0];
                const rawName = baseUrl.split('/').pop() || `文件 ${index + 1}`;
                let displayName = rawName;
                try { displayName = decodeURIComponent(rawName); } catch { /* keep raw */ }
                return (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-slate-600">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate" title={displayName}>
                        {displayName}
                      </p>
                    </div>
                    <span className="text-xs text-blue-600 shrink-0">查看</span>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            审核决定 *
          </label>
          <div className="space-y-3">
            {/* Approve Option */}
            <button
              onClick={() => setAction('approve')}
              className={`w-full p-4 border-2 rounded-xl text-left transition-all ${action === 'approve'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
                }`}
            >
              <div className="flex items-start gap-3">
                <CheckCircle
                  className={`w-5 h-5 mt-0.5 ${action === 'approve' ? 'text-green-600' : 'text-slate-400'
                    }`}
                />
                <div className="flex-1">
                  <p
                    className={`font-medium mb-1 ${action === 'approve' ? 'text-green-900' : 'text-slate-900'
                      }`}
                  >
                    批准交付物
                  </p>
                  <p
                    className={`text-sm ${action === 'approve' ? 'text-green-700' : 'text-slate-600'
                      }`}
                  >
                    确认交付物符合要求，将释放此里程碑的付款至创作者
                  </p>
                </div>
              </div>
            </button>

            {/* Revision Option */}
            <button
              onClick={() => setAction('revision')}
              className={`w-full p-4 border-2 rounded-xl text-left transition-all ${action === 'revision'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-slate-200 hover:border-slate-300'
                }`}
            >
              <div className="flex items-start gap-3">
                <AlertCircle
                  className={`w-5 h-5 mt-0.5 ${action === 'revision' ? 'text-amber-600' : 'text-slate-400'
                    }`}
                />
                <div className="flex-1">
                  <p
                    className={`font-medium mb-1 ${action === 'revision' ? 'text-amber-900' : 'text-slate-900'
                      }`}
                  >
                    请求修改
                  </p>
                  <p
                    className={`text-sm ${action === 'revision' ? 'text-amber-700' : 'text-slate-600'
                      }`}
                  >
                    交付物需要修改完善，创作者可以无限次重新提交
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Revision Feedback */}
        {action === 'revision' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              修改意见 *
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={6}
              placeholder="请详细说明需要修改的地方和期望效果，例如：&#10;&#10;1. 产品特写镜头需要更清晰&#10;2. 配乐节奏可以更快一些&#10;3. 最后的品牌logo展示时间延长到3秒"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
            />
            <p className="mt-2 text-xs text-slate-500">
              清晰具体的修改意见能帮助创作者更快完成优化
            </p>
          </div>
        )}

        {/* Warning for Approval */}
        {action === 'approve' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900 mb-1">付款提示</p>
                <p className="text-xs text-green-800">
                  批准后，此里程碑的款项将立即从托管账户释放给创作者，此操作不可撤销。请确认交付物符合您的要求后再批准。
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
